import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { createGlobalStyle } from 'styled-components';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import {
  isConsumerArea,
  selectAssetBucketingHeadline,
} from '../../../../CommonSelectors/CommonAreaSelector';
import { isOptedIn } from '../../../../CommonUtils/isOptedIn';
import { ClientFooter } from '../../../../ConsumerArea/components/Footer/FooterView';
import { PlanSelection } from '../../../../PlanSelectionOPL/PlanSelection';
import { useDocumentTitle } from '../../../../_hooks/useDocumentTitle';
import { Dropdown } from '../../components/Dropdown/Dropdown';
import { useFpId } from '../../../hooks/useFpId';
import { WhatIfs } from '../../whatIfs/WhatIfs';
import { ASSET_BUCKETING_DISABLE_ERROR } from './api/useAssetBucketingResultsQuery';
import { AssetBucketingResultsLoading } from './AssetBucketingResults.loading';
import { Breakdown } from './components/Breakdown/Breakdown';
import { useAssetBucketingResults } from './useAssetBucketingResults';
import { ExtraMenu } from './components/ExtraMenu/ExtraMenu';
import { ScrollHereOnce } from '../../../../CommonComponents/ScrollHereOnce';
import { useAreWhatIfsEnabled } from './hooks/useAreWhatIfsEnabled';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { useClientId } from '../../../../_hooks/useClientId';

const GliaGlobalStyle = createGlobalStyle<{ whatIfsOpened: boolean }>`
  #salemove .sm-visitor-app {
    right: ${({ whatIfsOpened }) => (whatIfsOpened ? '480px' : '30px')} !important;
  }
`;

interface Props {
  year: number;
}

export const AssetBucketingResults = ({ year }: Props) => {
  const areWhatIfsEnabled = useAreWhatIfsEnabled();

  const assetBucketingHeadline = useSelector(selectAssetBucketingHeadline);

  const [whatIfsOpened, setWhatIfsOpened] = useState(false);

  const isClientArea = useSelector(isConsumerArea);

  const clientId = useClientId();

  const fpId = useFpId();

  useDocumentTitle(assetBucketingHeadline);

  return (
    <Box display="flex" justifyContent="space-between" width="100%" flex={1} minHeight={0}>
      <Box flex={2} display="flex" flexDirection="column" overflowY="auto">
        <Box mx="auto" maxWidth={1200} width="100%">
          <PlanSelection />

          <Box width="100%" minWidth={1000} maxWidth={1200} p="32" m="0">
            <AssetBucketingResultsContent
              year={year}
              areWhatIfsEnabled={areWhatIfsEnabled}
              onWhatIfsClick={() => setWhatIfsOpened(!whatIfsOpened)}
            />
          </Box>
        </Box>

        {isClientArea && <ClientFooter />}
      </Box>

      <GliaGlobalStyle whatIfsOpened={whatIfsOpened} />

      {whatIfsOpened && areWhatIfsEnabled && (
        <WhatIfs
          clientId={clientId}
          fpId={fpId}
          whatIfAreaId="AssetBucketing"
          onClose={() => setWhatIfsOpened(false)}
        />
      )}
    </Box>
  );
};

interface AssetBucketingResultsContentProps {
  year: number;
  onWhatIfsClick(): void;
  areWhatIfsEnabled: boolean;
}

const AssetBucketingResultsContent: React.FC<AssetBucketingResultsContentProps> = ({
  year: routeYear,
  onWhatIfsClick,
  areWhatIfsEnabled,
}) => {
  const showAddGoalButton = isOptedIn('EditFinancialPlan');

  const clientId = useClientId();

  const {
    isLoading,
    isError,
    error,
    refetch,
    data,
    actions,
    isFetching,
  } = useAssetBucketingResults({ year: routeYear });

  if (isLoading || isFetching || !data?.yearOptions.find(opt => opt.value === data.year)) {
    return (
      <>
        <ScrollHereOnce options={false} />

        <AssetBucketingResultsLoading />
      </>
    );
  }

  if (isError) {
    if (error?.message === ASSET_BUCKETING_DISABLE_ERROR) {
      return (
        <Box flex={1} p="56" width="100%">
          <Alert variant="error" message={error.message} />
        </Box>
      );
    }

    return (
      <Box flex={1} mt="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const {
    buckets,
    stackedBarData,
    showFullBreakDown,
    description,
    title,
    isDefaultAssetBucketingLayout,
    allowAssetBucketingCustomization,
    yearOptions,
    year,
    netWorth,
  } = data;

  const { handleAddGoal, handleNetworthClick } = actions;

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box width={240}>
          <Dropdown defaultSelected={{ label: title, value: 'assetBucketing' }} />
        </Box>

        <Box display="flex" alignItems="center" gridGap="8" justifyContent="space-between">
          {areWhatIfsEnabled && data.yearOptions.length > 1 && (
            <Select
              isSearchable={false}
              placeholder="Additional Sale Years"
              value={yearOptions.find(opt => opt.value === year)}
              options={yearOptions}
              onChange={opt =>
                opt?.value &&
                ADV.util.Router.go2('AssetBucketingResults', { clientId, year: opt?.value })
              }
              noWrap
            />
          )}

          {showAddGoalButton && (
            <Button variant="secondary" leftIcon="fas fa-bullseye-arrow" onClick={handleAddGoal}>
              Add Goal
            </Button>
          )}

          {areWhatIfsEnabled && (
            <Button leftIcon="fas fa-sliders-h" onClick={onWhatIfsClick}>
              What-Ifs
            </Button>
          )}

          {allowAssetBucketingCustomization && <ExtraMenu clientId={clientId} />}
        </Box>
      </Box>

      <Breakdown
        title={title}
        description={description}
        networthAmount={netWorth}
        onNetworthClick={handleNetworthClick}
        networthMakeup={stackedBarData}
        buckets={buckets}
        showFullBreakdown={showFullBreakDown}
        isDefaultAssetBucketingLayout={isDefaultAssetBucketingLayout}
        hasExcludedFromNetworth={data.hasExcludedFromNetworth}
      />
    </>
  );
};
